<template>
  <article
    class="rt-mobile-card"
    :class="{
      'rt-mobile-card--open': room.isOpen && isManualCloseSla,
      'rt-mobile-card--expanded': isExpanded,
      'rt-mobile-card--offline': isOfflinePad || isOfflineTablet,
    }"
    @click="$emit('card-click')"
  >
    <header class="rt-mobile-card__header">
      <h3 class="rt-mobile-card__name">{{ displayName }}</h3>
      <div class="rt-mobile-card__header-icons">
        <RealTimeTableCell
          v-if="hasField('gender')"
          class="rt-mobile-card__icon-cell"
          columnName="gender"
          :slaData="room"
          :maxSla="maxSla"
          :isMaintenance="isMaintenance"
          :companyGmt="companyGmt"
        />
        <RealTimeTableCell
          v-if="hasField('sla_status')"
          class="rt-mobile-card__icon-cell"
          columnName="sla_status"
          :slaData="room"
          :maxSla="maxSla"
          :isMaintenance="isMaintenance"
          :companyGmt="companyGmt"
        />
      </div>
    </header>

    <dl v-if="detailFields.length" class="rt-mobile-card__metrics">
      <div
        v-for="field in detailFields"
        :key="field"
        :class="['rt-mobile-card__metric', `rt-mobile-card__metric--${field}`]"
      >
        <dt class="rt-mobile-card__metric-label">{{ getColumnHeaderLabel(field) }}</dt>
        <dd class="rt-mobile-card__metric-value">
          <RealTimeTableCell
            :columnName="field"
            :slaData="room"
            :maxSla="maxSla"
            :isMaintenance="isMaintenance"
            :companyGmt="companyGmt"
          />
        </dd>
      </div>
    </dl>

    <div v-if="hasField('call')" class="rt-mobile-card__metric rt-mobile-card__metric--call">
      <span class="rt-mobile-card__metric-label">{{ $t('call') }}</span>
      <div class="rt-mobile-card__metric-value">
        <RealTimeTableCell
          class="rt-mobile-card__calls-cell"
          columnName="call"
          :slaData="room"
          :maxSla="maxSla"
          :isMaintenance="isMaintenance"
          :companyGmt="companyGmt"
        />
      </div>
    </div>

    <div v-if="room.isOpen && isManualCloseSla" class="rt-mobile-card__actions" @click.stop>
      <button v-if="isTask" type="button" class="rt-mobile-card__action-btn" @click="$emit('start-task', room)">
        <img src="@/assets/start-icon.svg" alt="" />
      </button>
      <button type="button" class="rt-mobile-card__action-btn" @click="$emit('close-click', room)">
        <img src="@/assets/x-icon.svg" alt="" />
      </button>
      <button
        v-if="isTask"
        type="button"
        class="rt-mobile-card__action-btn rt-mobile-card__action-btn--delete"
        :title="$t('delete')"
        @click="$emit('delete-open-task', room)"
      >
        <img src="@/assets/delete-icon.svg" alt="" />
      </button>
    </div>
  </article>
</template>

<script>
import RealTimeTableCell from './RealTimeTableCell'
import { isOfflineTabletDisplay } from '../utils/deviceConnectivityUtils'

export default {
  name: 'RealTimeMobileCard',
  components: { RealTimeTableCell },
  props: {
    room: { type: Object, required: true },
    fields: { type: Array, required: true },
    maxSla: { type: Number, required: false },
    isMaintenance: { type: Boolean, default: false },
    isTask: { type: Boolean, default: false },
    companyGmt: { type: [Number, String], default: null },
    isManualCloseSla: { type: Boolean, default: false },
    isExpanded: { type: Boolean, default: false },
  },
  computed: {
    displayName() {
      const name = this.room && this.room.name
      if (!name) return '—'
      const translated = this.$t(name)
      return translated !== name ? translated : name
    },
    detailFields() {
      const headerFields = ['name', 'gender', 'sla_status', 'call']
      return this.fields.filter((field) => !headerFields.includes(field))
    },
    isOfflinePad() {
      const { sensorState, sensorType } = this.room || {}
      const isPad = sensorType === 'pad9' || sensorType === 'pad9+'
      return isPad && (!sensorState || sensorState === 'offline')
    },
    isOfflineTablet() {
      return isOfflineTabletDisplay(this.room)
    },
  },
  methods: {
    hasField(field) {
      return this.fields.includes(field)
    },
    getColumnHeaderLabel(title) {
      if (title === 'sla_status') return this.$t('device_status')
      if (title === 'arrival') return this.$t('last_arrival')
      if (title === 'arrival_badges') return this.$t('arrival_badges')
      if (title === 'visits_since_cleaned') return this.$t('visits_since_cleaned')
      if (title === 'temperature') {
        const abbr = this.$t('temperature_abbr')
        return abbr !== 'temperature_abbr' ? abbr : this.$t('temperature')
      }
      if (title === 'humidity') {
        const abbr = this.$t('humidity_abbr')
        return abbr !== 'humidity_abbr' ? abbr : this.$t('humidity')
      }
      return this.$t(title)
    },
  },
}
</script>

<style lang="scss" scoped>
.rt-mobile-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;

  &--offline {
    background-color: #f5f4f4;
  }

  &--open {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  }

  &--expanded {
    border-color: #94a3b8;
  }
}

.rt-mobile-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.rt-mobile-card__name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.rt-mobile-card__header-icons {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.rt-mobile-card__icon-cell {
  padding: 0;
}

.rt-mobile-card__metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.rt-mobile-card__metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  min-width: 0;

  &--call {
    margin-top: 2px;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
  }
}

.rt-mobile-card__metric-label {
  flex: 1 1 auto;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #64748b;
  text-align: start;
  line-height: 1.3;
  min-width: 0;
}

.rt-mobile-card__metric-value {
  flex: 0 0 auto;
  margin: 0;
  margin-inline-start: auto;
  font-size: 13px;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  text-align: end;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ::v-deep .real-time-table-cell {
    padding: 0;
    font-size: inherit;
    text-align: end;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: auto;
  }

  ::v-deep .arrival-badges,
  ::v-deep .arrival-badges-column .arrival-badges {
    justify-content: flex-end;
  }

  ::v-deep .arrival .arrival-time-only {
    text-align: end;
  }

  ::v-deep .call-cell-container {
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 4px;
  }
}

.rt-mobile-card__calls-cell {
  padding: 0;
}

.rt-mobile-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.rt-mobile-card__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;

  img {
    width: 18px;
    height: 18px;
  }

  &--delete img {
    width: 16px;
    height: 16px;
  }
}
</style>
